const NAMESPACE = "login";

const KEYS = {
    powerclerkHeader: "powerclerkHeader",
    loginPaneHeader: "loginPaneHeader",
    mfaPaneHeader: "mfaPaneHeader",
    usernameInputLabel: "usernameInputLabel",
    usernamePlaceholder: "usernamePlaceholder",
    passwordInputLabel: "passwordInputLabel",
    loginBtnLabel: "loginBtnLabel",
    forgotPasswordLinkText: "forgotPasswordLinkText",
    selfRegisterLinkText: "selfRegisterLinkText",
    selfRegisterText2: "selfRegisterText2",
    mfaInputLabel: "mfaInputLabel",
    verifyMfaCodeBtnLabel: "verifyMfaCodeBtnLabel",
    mfaHelpLinkLabel: "mfaHelpLinkLabel",
    resendConfirmationLinkText: "resendConfirmationLinkText",
    loginErrorUnconfirmed: "loginErrorUnconfirmed",
    loginErrorLockedOut: "loginErrorLockedOut",
    loginErrorIncorrectCredentials: "loginErrorIncorrectCredentials",
    loginErrorNoMappedPrograms: "loginErrorNoMappedPrograms",
    loginErrorOtherError: "loginErrorOtherError",
    loginErrorMfaCodeInvalid: "loginErrorMfaCodeInvalid",
    loginErrorMfaSecretLocked: "loginErrorMfaSecretLocked",
    loginErrorSsoRequiredForUserInProgram: "loginErrorSsoRequiredForUserInProgram",
    confirmationResentAlertText: "confirmationResentAlertText",
    differentProgramRedirect: "differentProgramRedirect",
    deactivatedUserRedirect: "deactivatedUserRedirect",
    retryLoginButtonText: "retryLoginButtonText",
    clickHereText: "clickHereText"
};

const MESSAGES = {
    [KEYS.powerclerkHeader]: {
        value: "PowerClerk",
        comment: "Header showing the name of the web application the user about to log into"
    },
    [KEYS.loginPaneHeader]: {
        value: "Log In",
        comment: "Header of the login panel indicating that's the form the user will use to log in"
    },
    [KEYS.mfaPaneHeader]: {
        value: "Multi-Factor Authentication",
        comment:
            "Header of the MFA panel indicating the the user needs to input an additional code to complete logging in"
    },
    [KEYS.usernameInputLabel]: {
        value: "Username:",
        comment: "Label for the input where the user enters their username (an email address) to log in"
    },
    [KEYS.usernamePlaceholder]: {
        value: "example{'@'}company.com",
        comment: "Placeholder email address that goes in the username input to indicate the format of a valid email"
    },
    [KEYS.passwordInputLabel]: {
        value: "Password:",
        comment: "Label for the input where the user enters their password to log in"
    },
    [KEYS.loginBtnLabel]: {
        value: "Log In",
        comment: "Label for the button that the user clicks to log in after entering their username and password"
    },
    [KEYS.forgotPasswordLinkText]: {
        value: "Forgot Password?",
        comment:
            "Text of the link that takes the user to the forgot password page which allows them to reset their password"
    },
    [KEYS.selfRegisterLinkText]: {
        value: "Register",
        comment: "Text of a link that allows users to create a new account for one or more PowerClerk programs"
    },
    [KEYS.selfRegisterText2]: {
        value: "a new account",
        comment:
            "Non-clickable text that immediately follows the self-register link which indicates to a user that they can click a link to register a new account for a program"
    },
    [KEYS.mfaInputLabel]: {
        value: "6-digit code from your device:",
        comment:
            "Label for the input where the user enters their MFA code to complete logging in if their account has MFA enabled"
    },
    [KEYS.verifyMfaCodeBtnLabel]: {
        value: "Verify Code",
        comment:
            "Label for the button that the user clicks after entering their MFA code to complete logging in for an MFA-enabled account"
    },
    [KEYS.mfaHelpLinkLabel]: {
        value: "Need Help?",
        comment:
            "Text of a link that appears when the user is prompted to enter their MFA code which will display helptext describing how the user can complete logging into their MFA-enabled account"
    },
    [KEYS.resendConfirmationLinkText]: {
        value: "Resend confirmation",
        comment: "Text of the link that allows users to resend an email to confirm their newly-created account"
    },
    [KEYS.loginErrorUnconfirmed]: {
        value: "User has not yet been confirmed.",
        comment: "Alert text explaining that the log in failed because the user has not yet confirmed their account"
    },
    [KEYS.loginErrorLockedOut]: {
        value: 'The account is locked. To unlock, reset password by clicking "Forgot Password?".',
        comment:
            "Alert text explaining that the log in failed because the user's account is locked after too many incorrect login attempts"
    },
    [KEYS.loginErrorIncorrectCredentials]: {
        value: "Incorrect username or password.",
        comment: "Alert text explaining that the login failed because the username or password was incorrect"
    },
    [KEYS.loginErrorNoMappedPrograms]: {
        value: "User has no registered programs.",
        comment:
            "Alert text explaining that the login failed because the user's account exists but they are not currently registered for any programs"
    },
    [KEYS.loginErrorOtherError]: {
        value: "Login failure. Please contact your PowerClerk administrator.",
        comment:
            "Alert text explaining that the login failed for an unusual reason and that they will likely have to contact their administrator to get it resolved"
    },
    [KEYS.loginErrorMfaCodeInvalid]: {
        value: "Invalid Multi-Factor Authentication code: please try again. If the problem persists, contact your PowerClerk administrator.",
        comment:
            "Alert text explaining that the login failed because the MFA code was incorrect and that the user should either retry with a new code or contact their program administrator"
    },
    [KEYS.loginErrorMfaSecretLocked]: {
        value: "For your security, your account has been temporarily locked due to repeated failures. Please try again in a few minutes.",
        comment:
            "Alert text explaining that the login failed they have MFA enabled but there were too many invalid attempts to use their MFA code within a certain amount of time"
    },
    [KEYS.loginErrorSsoRequiredForUserInProgram]: {
        value: "This program requires Single Sign-On authentication.",
        comment:
            "Alert text explaining that the user must log in through an external identity provider for this program since it has Single-Sign On enabled and doesn't allow directly logging in via PowerClerk"
    },
    [KEYS.confirmationResentAlertText]: {
        value: "Confirmation resent",
        comment: "Alert text that appears when the user chooses to resend the account confirmation email"
    },
    [KEYS.differentProgramRedirect]: {
        value: "Your account does not have a role in this program. To log into the program last accessed by your account, click here: {redirectLinkText}.",
        comment:
            "Alert text that appears when a user attempts to log into a program that does not have a role for the user, but a different program was found associated with that user. redirectLinkText is the 'Agency - Display' name of the old program."
    },
    [KEYS.deactivatedUserRedirect]: {
        value: "Your account does not have a role in any program. To access your account settings, {redirectLinkText}.",
        comment:
            "Alert text that appears when a user attempts to log in when they do not have access to any program in PowerClerk. redirectLinkText is the value of the KEY 'clickHereText'"
    },
    [KEYS.retryLoginButtonText]: {
        value: "Use Different Login",
        comment: "Text for button on redirect prompt to return to login screen"
    },
    [KEYS.clickHereText]: {
        value: "click here",
        comment: "Text for link to redirect location."
    }
};

export { KEYS, NAMESPACE, MESSAGES };
